<script>
import { updateCoupon as updateCouponApi } from "@/api/store/CouponsService.js";
import { getCoupon } from "@/api/store/CouponsService";
import CouponProducts from "./CouponProducts.vue";
import CouponCategories from "./CouponCategories.vue";
import CouponForm from "./CouponForm.vue";
import { couponDiscountText } from "./couponLabels.js";

export default {
  name: "EditCoupon",
  components: {
    CouponProducts,
    CouponCategories,
    CouponForm
  },
  props: {
    coupon: {
      type: Object,
      required: true
    },
    initialTab: {
      type: String,
      default: "general"
    }
  },
  data() {
    return {
      tab: this.initialTab,
      original: null,
      newEntry: null,
      response: null,
      saving: false
    };
  },
  computed: {
    changed() {
      return Object.keys(this.newEntry).some(
        key =>
          key in this.original &&
          String(this.newEntry[key] ?? "") !== String(this.original[key] ?? "")
      );
    },
    tabs() {
      const tabs = [{ code: "general", name: "General" }];
      if (this.original && this.original.discountType === "product") {
        tabs.push({ code: "products", name: "Products" });
        tabs.push({ code: "categories", name: "Categories" });
      }
      return tabs;
    },
    discountText() {
      return this.original ? couponDiscountText(this.original) : "";
    }
  },
  mounted() {
    this.loadCoupon();
  },
  methods: {
    loadCoupon() {
      getCoupon(this.coupon.id).then(res => {
        this.original = res;
        this.newEntry = { ...res };
      });
    },
    update() {
      this.response = null;
      this.saving = true;
      updateCouponApi(this.coupon.id, this.newEntry)
        .then(res => {
          this.saving = false;
          if (res) {
            this.$toast.success("Coupon updated successfully");
            this.$emit("updated");
          } else {
            this.$toast.error("Error updating coupon");
          }
        })
        .catch(e => {
          this.response = e.response ? e.response.data : e.message;
          this.$toast.error(this.response);
          this.saving = false;
        });
    }
  }
};
</script>
<template>
  <BaseModal :value="true" @input="$emit('close')" id="edit-coupon-modal">
    <template #header>
      <div v-if="original" class="flex items-baseline gap-3">
        <h2 class="text-lg font-semibold font-mono">{{ original.code }}</h2>
        <span class="text-gray-500">{{ discountText }}</span>
      </div>
    </template>
    <div v-if="!newEntry" class="p-4"><BaseSmallSpinner /></div>
    <template v-else>
      <div class="flex gap-1 border-b mb-4 px-2">
        <button
          v-for="t in tabs"
          :key="t.code"
          type="button"
          class="px-4 py-2 -mb-px border-b-2 font-medium"
          :class="
            tab === t.code
              ? 'border-gray-800 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          "
          @click="tab = t.code"
        >
          {{ t.name }}
        </button>
      </div>
      <div class="px-2 pb-2">
        <div v-show="tab === 'general'">
          <div v-if="response" class="bg-red-200 w-full p-2 rounded-md mb-2">
            {{ response }}
          </div>
          <CouponForm
            v-model="newEntry"
            @confirm="update()"
            @discard="$emit('close')"
            :create="false"
            :changed="changed"
            :saving="saving"
          />
        </div>
        <CouponProducts
          v-if="tab === 'products'"
          :id="String(coupon.id)"
          :code="original.code"
          @changed="$emit('rulesChanged')"
        />
        <CouponCategories
          v-if="tab === 'categories'"
          :id="String(coupon.id)"
          @changed="$emit('rulesChanged')"
        />
      </div>
    </template>
  </BaseModal>
</template>
<style>
#edit-coupon-modal .modal-content {
  max-width: 900px;
  max-height: 90%;
  overflow-y: auto;
}
</style>
